import React, { useEffect, useState, Fragment, useContext, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import Widget from '../widgets/Widget';
import { apiUrl } from '../api/endpoints';
import { useAuth } from '../hooks/AuthProvider';
import useWindowDimensions from '../hooks/useWindowDimensions';
import '../Components/modal.css';
import './details.css';
import './WebDashboard.css';
import '../widgets/SegmentSwitch.css';
import { LanguageContext } from '../context/LanguageContext';
import webDashboardTranslations from '../languages/webDashboard.json';
import { buildDeviceDropdownOption } from '../utils/dropdownLabels';
import { getThemeColorKey, getThemeColorValue, normalizeThemeMode } from '../utils/themeColors';
import {
	ENVIRONMENTAL_STATUS_WIDGET_TYPE,
} from '../widgets/environmentalStatusConfig';
import {
	normalizeWebDashboardLayoutMode,
	WEB_DASHBOARD_LAYOUT_MODES,
} from '../utils/webDashboardLayout';
import { getNextTemporaryDatastreamId } from '../utils/datastreamIds';
import {
	buildWaterNetworkConnections,
	canWaterWidgetsShareSpace,
	isWidgetFreeMovementEnabled,
	isWaterNetworkWidgetType,
	isWaterNetworkLinkEnabled,
	snapWaterNetworkMoveRect,
	snapWaterNetworkResizeRect,
	WATER_NETWORK_FREEFORM_SEARCH_STEP,
} from '../utils/waterNetworkLayout';
import { EditDashboardCanvas, ViewDashboardCanvas } from './WebDashboard/canvas/DashboardCanvas';
import WebDashboardEditToggles from './WebDashboard/canvas/WebDashboardEditToggles';
import WebDashboardFeedback from './WebDashboard/canvas/WebDashboardFeedback';
import WidgetDatastreamEditor from './WebDashboard/settings/WidgetDatastreamEditor';
import WidgetSettingsContent from './WebDashboard/settings/WidgetSettingsContent';
import WidgetSettingsEditorPanel from './WebDashboard/settings/WidgetSettingsEditorPanel';
import WidgetSettingsFooter from './WebDashboard/settings/WidgetSettingsFooter';
import WidgetSettingsModal from './WebDashboard/settings/WidgetSettingsModal';
import WidgetSettingsPreview from './WebDashboard/settings/WidgetSettingsPreview';
import WidgetToolbox from './WebDashboard/canvas/WidgetToolbox';
import WebDashboardMainContainer from './WebDashboard/canvas/WebDashboardMainContainer';
import {
	EDITABLE_TRAILING_ROWS,
	getDefaultWidgetDimensions,
	getMinimumWidgetSizeForType,
	getSpannedWidgetSize,
	isHiddenWidgetEditorPropKey,
} from './WebDashboard/layout/webDashboardConstants';
import {
	cloneDashboardValue,
	formatPercentValue as formatPercentValueValue,
	getCanvasBottomBufferPx,
	getElementViewportHeight,
	getNearestGridRect as getNearestGridRectValue,
	hasStoredPercentageGeometry as hasStoredPercentageGeometryValue,
	parseStoredDimension as parseStoredDimensionValue,
	parseStoredPosition as parseStoredPositionValue,
	sameMeasuredSize,
	setElementPixelStyle,
	toPxNumber as toPxNumberValue,
} from './WebDashboard/layout/webDashboardGeometry';
import { getWebDashboardWidgetDefaults } from './WebDashboard/layout/webDashboardWidgetDefaults';
import { getClosedWidgetEditState } from './WebDashboard/helpers/webDashboardEditState';
import {
	applyDatastreamDefaultColors as applyDatastreamDefaultColorsValue,
	buildFontSizeFields,
	buildPeriodOptions,
	filterWidgetOptionsByQuery,
	formatGeometryDraftValue as formatGeometryDraftValueValue,
	formatPinForTitle,
	getDeviceTemplateSignature as getDeviceTemplateSignatureValue,
	getEditContainingBackground,
	getSafeDatastreamList,
	getSelectedDatastreamForEdit,
	hasWidgetColorSettings,
	normalizeLegacyWidgetDimensions,
	preserveTitleSpaces,
	roundGeometryValue,
	stripDatastreamManagedProps as stripDatastreamManagedPropsValue,
	toLengthInputNumber,
} from './WebDashboard/helpers/webDashboardEditorHelpers';
import { getRemoveBackgroundDialogMessage } from './WebDashboard/helpers/webDashboardFeedbackMessages';
import { getDefaultWidgetTitle } from './WebDashboard/helpers/webDashboardWidgetTitles';
import {
	clampToMinMax as clampValueToDatastreamMinMax,
	findDatastreamById as findDatastreamByIdValue,
	findDatastreamByReference as findDatastreamByReferenceValue,
	getDatastreamMinMax,
	getEffectiveMinMax as getEffectiveDatastreamMinMax,
	isStringDatastreamValue,
	isNumericDatastreamValue,
	normalizeSliderValueInputChange as normalizeSliderValueInputChangeValue,
	normalizeDatastreamPin as normalizeDatastreamPinValue,
	remapColorStopsForDatastream as remapColorStopsForDatastreamValue,
	snapValueToStep as snapValueToDatastreamStep,
} from './WebDashboard/helpers/webDashboardDatastreamHelpers';
import {
	WEB_DASHBOARD_WIDGET_PALETTE,
	buildDefaultWidgetPropsByType,
	buildWidgetPalettePropsByKey,
	getWidgetPropsFromType,
	getWidgetPaletteTitle,
	sortWebDashboardWidgets,
} from './WebDashboard/layout/webDashboardWidgetRegistry';
import {
	DASHBOARD_WIDGET_FONT_SIZES_UPDATED_EVENT,
	getCachedDashboardWidgetFontSizes,
	normalizeDashboardWidgetFontSizes,
	setCachedDashboardWidgetFontSizes,
} from '../utils/dashboardWidgetFontSizes';
import {
	getClampedSelectedGroupDelta,
	getSelectedGroupBounds,
	getSelectedGroupMoveMetrics,
	getSelectedGroupMovedRects,
	getSelectedWidgetIdSet,
	getSelectedWidgetRectEntries,
} from './WebDashboard/layout/webDashboardSelectionGeometry';
import {
	getDropSearchGridSize,
	getDropSearchStep,
	getDropBounds,
	getDropContainerStyleMetrics,
	getDropWidgetSize,
	getInitialDropPoint,
	getNearestAvailableDropCandidate,
	getRelativeDropPoint,
	getSnappedWaterNetworkDropPoint,
	isImmediateDropAllowedWidget,
	isDropCandidateWithinBounds,
	shouldShowNoDropSpaceAlert,
	shouldSnapWaterNetworkDropPoint,
} from './WebDashboard/layout/webDashboardDropGeometry';
import {
	findNearestEmptySpotForMetrics,
} from './WebDashboard/layout/webDashboardDuplication';
import {
	buildNewDashboardWidget,
	getConstrainedOrFallbackRect,
	isInvalidWidgetOverlap,
} from './WebDashboard/layout/webDashboardDropCommit';
import {
	createDuplicateWidgetHandler,
	createResizeWidgetHandler,
} from './WebDashboard/handlers/webDashboardWidgetHandlers';
import { createBackgroundRemovalHandlers } from './WebDashboard/handlers/webDashboardBackgroundHandlers';
import { createSelectionHandlers } from './WebDashboard/handlers/webDashboardSelectionHandlers';
import { createDropHandler } from './WebDashboard/handlers/webDashboardDropHandler';
import { createCollisionHandlers } from './WebDashboard/handlers/webDashboardCollisionHandlers';
import {
	createEditPropHandlers,
	createSettingsHandlers,
} from './WebDashboard/handlers/webDashboardSettingsHandlers';
import {
	createGeometryDraftHandlers,
	runFixedWidthToggle,
} from './WebDashboard/handlers/webDashboardGeometryHandlers';
import { runTerminalCleared } from './WebDashboard/handlers/webDashboardTerminalHandlers';

const WebDashboard = ({
	dashboard,
	setDashboard,
	datastreams,
	setDatastreams,
	mode,
	maxWidth,
	maxHeight,
	did,
	tid,
	devices,
	externalFixedWidthControl = false,
	onFixedWidthModeChange,
	onFixedWidthToggleHandlerReady,
	externalMultiSelectControl = false,
	onMultiSelectToggleHandlerReady,
	onMultiSelectStateChange,
}) => {

	const { lang } = useContext(LanguageContext);
	const t = webDashboardTranslations[lang]?.webDashboard || {};
	const directionLabels = t.directionOptions || {};
	const [deviceDatastream, setDeviceDatastream] = useState([]);
	const [divs, setDivs] = useState([]);
	const [addDatastream, setAddDatastream] = useState({
		edit: false,
		pinCategory: ''
	}),
		[showAlert, setShowAlert] = useState(false),
		[alertMessage, setAlertMessage] = useState(''),
		[alertType, setAlertType] = useState('error'),
		[alertPosition] = useState('right'),
		[isFixedWidthMode, setIsFixedWidthMode] = useState(dashboard?.fixedWidth ?? true),
		[fixedWidthGuide, setFixedWidthGuide] = useState(null),
		[lastWidgetsBoxWidth, setLastWidgetsBoxWidth] = useState(0),
		[fixedViewWidth, setFixedViewWidth] = useState(0),
		[viewPercentageCanvas, setViewPercentageCanvas] = useState({ width: 0, height: 0 }),
		[layoutMeasureVersion, setLayoutMeasureVersion] = useState(0);
	const [removeBackgroundDialog, setRemoveBackgroundDialog] = useState({
		open: false,
		backgroundId: null,
		affectedWidgetIds: [],
		affectedCount: 0,
	});
	const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);
	const [selectedWidgetIds, setSelectedWidgetIds] = useState([]);
	const [selectionRect, setSelectionRect] = useState(null);
	const [selectedGroupDragOffset, setSelectedGroupDragOffset] = useState(null);
	const [widgetSearchQuery, setWidgetSearchQuery] = useState('');
	const [dashboardWidgetFontSizes, setDashboardWidgetFontSizes] = useState(() =>
		normalizeDashboardWidgetFontSizes(getCachedDashboardWidgetFontSizes() || {})
	);

	const [templateEvaluationData, setTemplateEvaluationData] = useState({
		evaluation_names: [],
		datastream_ranges: []
	});

	const handleTerminalCleared = useCallback((payload) => runTerminalCleared({
		dashboard,
		payload,
		setDashboard,
	}), [dashboard, setDashboard]);

	const alignments = t.alignments ?? [],
		aggregations = t.aggregations ?? [],
		chartTypes = t.chartTypes ?? [],
		periodOptions = buildPeriodOptions(t.periodOptions),
		periodStats = t.periodStats ?? [
			{ label: 'Min', value: 'min' },
			{ label: 'Max', value: 'max' },
		],
		[edit, setEdit] = useState(getClosedWidgetEditState),
		[widgetLiveProps, setWidgetLiveProps] = useState({}),
		{ permissions } = useAuth(),
		defaultDivWidth = 24, defaultDivHeight = 120,
		dims = useWindowDimensions(),
		defaultWidth = 248, defaultHeight = 120,
		{
			defaultChartProps,
			defaultPeriodColumnProps,
			previewPeriodColumnProps,
			defaultGaugeProps,
			defaultLiquidProps,
			defaultWaterTankProps,
			defaultRadialGaugeProps,
			defaultLabelProps,
			defaultLEDProps,
			defaultTextTitleProps,
			defaultTimeRangeProps,
			defaultEnvironmentalStatusProps,
			defaultNumberProps,
			defaultSliderProps,
			defaultSwitchProps,
			defaultPushButtonProps,
			defaultTerminalProps,
			defaultKeypadProps,
			defaultMenuProps,
			defaultSegmentSwitchProps,
			defaultImageButtonProps,
			defaultBackgroundImageProps,
			defaultTextProps,
			defaultTableProps,
			defaultFanProps,
			defaultPipeProps,
			defaultElbowProps,
			defaultWaterPumpProps,
			defaultBoxWidgetProps,
			defaultCameraStreamProps,
			defaultAssetStatusProps,
			defaultStatusArrowProps,
		} = getWebDashboardWidgetDefaults(t, dashboardWidgetFontSizes),
		defaultWidgetPropsByType = buildDefaultWidgetPropsByType({
			defaultChartProps,
			defaultPeriodColumnProps,
			defaultGaugeProps,
			defaultLiquidProps,
			defaultWaterTankProps,
			defaultRadialGaugeProps,
			defaultLabelProps,
			defaultLEDProps,
			defaultTextTitleProps,
			defaultTimeRangeProps,
			defaultEnvironmentalStatusProps,
			defaultNumberProps,
			defaultSliderProps,
			defaultSwitchProps,
			defaultPushButtonProps,
			defaultTerminalProps,
			defaultKeypadProps,
			defaultMenuProps,
			defaultSegmentSwitchProps,
			defaultImageButtonProps,
			defaultBackgroundImageProps,
			defaultTextProps,
			defaultTableProps,
			defaultFanProps,
			defaultPipeProps,
			defaultElbowProps,
			defaultWaterPumpProps,
			defaultBoxWidgetProps,
			defaultCameraStreamProps,
			defaultAssetStatusProps,
			defaultStatusArrowProps,
		}),
		widgetPalettePropsByKey = buildWidgetPalettePropsByKey({
			defaultChartProps,
			previewPeriodColumnProps,
			defaultGaugeProps,
			defaultLiquidProps,
			defaultWaterTankProps,
			defaultRadialGaugeProps,
			defaultLabelProps,
			defaultLEDProps,
			defaultNumberProps,
			defaultSliderProps,
			defaultSwitchProps,
			defaultPushButtonProps,
			defaultTerminalProps,
			defaultTextProps,
			defaultMenuProps,
			defaultSegmentSwitchProps,
			defaultImageButtonProps,
			defaultBackgroundImageProps,
			defaultKeypadProps,
			defaultTableProps,
			defaultFanProps,
			defaultPipeProps,
			defaultWaterPumpProps,
			defaultElbowProps,
			defaultTextTitleProps,
			defaultTimeRangeProps,
			defaultEnvironmentalStatusProps,
			defaultBoxWidgetProps,
			defaultCameraStreamProps,
			defaultAssetStatusProps,
			defaultStatusArrowProps,
		});

	useEffect(() => {
		let cancelled = false;

		const applyConfig = (nextConfig) => {
			if (!cancelled) {
				setDashboardWidgetFontSizes(normalizeDashboardWidgetFontSizes(nextConfig || {}));
			}
		};

		const handleWidgetFontSizesUpdated = (event) => {
			applyConfig(event?.detail || {});
		};

		if (typeof window !== 'undefined') {
			window.addEventListener(DASHBOARD_WIDGET_FONT_SIZES_UPDATED_EVENT, handleWidgetFontSizesUpdated);
		}

		axios
			.get(apiUrl('configuration/public'), { params: { _: Date.now() } })
			.then((res) => {
				const nextConfig = setCachedDashboardWidgetFontSizes(
					res.data?.data?.dashboardWidgetFontSizes || {}
				);
				applyConfig(nextConfig);
			})
			.catch((error) => {
				console.error('[WebDashboard] Failed to load widget font size defaults:', error);
				applyConfig(getCachedDashboardWidgetFontSizes() || {});
			});

		return () => {
			cancelled = true;
			if (typeof window !== 'undefined') {
				window.removeEventListener(DASHBOARD_WIDGET_FONT_SIZES_UPDATED_EVENT, handleWidgetFontSizesUpdated);
			}
		};
	}, []);

	const colorThemeMode = normalizeThemeMode(edit?.props?.themeMode);
	const deviceSelectOptions = useMemo(
		() => (Array.isArray(devices) ? devices : []).map((device) => buildDeviceDropdownOption(device)),
		[devices]
	);
	const themeColorValue = (obj, key, fallback) =>
		getThemeColorValue(obj, key, colorThemeMode, fallback);
	const themeColorKey = (key) => getThemeColorKey(key, colorThemeMode);
	const getNextDatastreamId = (list) => {
		return getNextTemporaryDatastreamId(list);
	};
	const hasColorSettings = hasWidgetColorSettings(edit?.props);
	const {
		handleFontSizeBlur,
		handleFontSizeChange,
		sanitizeWidgetFontSizeProps,
	} = useMemo(() => createEditPropHandlers({
		edit,
		setEdit,
	}), [edit]);
	const fontSizeFields = buildFontSizeFields(t.settingsLabels, edit?.type);
	const layoutMode = normalizeWebDashboardLayoutMode(dashboard?.layoutMode);
	const isPercentageLayout = layoutMode === WEB_DASHBOARD_LAYOUT_MODES.PERCENTAGE;

	const toPxNumber = useCallback(toPxNumberValue, []),
		getPercentageViewportMetrics = useCallback(() => {
			if (mode === 'edit') {
				const mainContainer = document.getElementById('web-dashboard-main-container');
				const dashboardElement = document.getElementById('diverted');
				const widgetsBox = document.querySelector('.widgets-box');
				const containerGap = 8;

				const availableWidth = mainContainer
					? Math.max(
						1,
						mainContainer.offsetWidth - (widgetsBox?.offsetWidth || 0) - containerGap,
					)
					: Math.max(
						1,
						dashboardElement?.clientWidth || dashboardElement?.getBoundingClientRect?.().width || 0,
					);
				const availableHeight = mainContainer
					? getElementViewportHeight(mainContainer)
					: Math.max(
						1,
						dashboardElement?.clientHeight || dashboardElement?.getBoundingClientRect?.().height || 0,
					);

				return {
					width: availableWidth,
					height: availableHeight,
				};
			}

			const dashboardElement = document.getElementById('diverted-view');
			if (!dashboardElement) {
				return null;
			}

			const rect = dashboardElement.getBoundingClientRect();
			return {
				width: Math.max(1, dashboardElement.clientWidth || rect.width || 0),
				height: Math.max(1, dashboardElement.clientHeight || rect.height || 0),
			};
		}, [mode]),
		formatPercentValue = useCallback(formatPercentValueValue, []),
		hasStoredPercentageGeometry = useCallback((widget) => (
			hasStoredPercentageGeometryValue(widget)
		), []),
		parseStoredDimension = useCallback(parseStoredDimensionValue, []),
		getPercentageCoordinateSpace = useCallback((metrics, percentageReference = null) => {
			const reference = percentageReference || metrics?.percentageReference || getPercentageViewportMetrics();
			const referenceWidth = Math.max(1, reference?.width || metrics?.width || 1);
			const referenceHeight = Math.max(1, reference?.height || metrics?.height || 1);
			const originLeft = Math.max(0, metrics?.padLeft || 0);
			const originTop = Math.max(0, metrics?.padTop || 0);
			const padRight = Math.max(0, metrics?.padRight || 0);
			const padBottom = Math.max(0, metrics?.padBottom || 0);

			return {
				originLeft,
				originTop,
				width: Math.max(1, referenceWidth - originLeft - padRight),
				height: Math.max(1, referenceHeight - originTop - padBottom),
			};
		}, [getPercentageViewportMetrics]),
		parseStoredPosition = useCallback(parseStoredPositionValue, []),
		getPercentageReferenceMetrics = useCallback(() => (
			getPercentageViewportMetrics()
		), [getPercentageViewportMetrics]),
		getEditableMetrics = useCallback(() => {
			const editableElement = document.getElementById('editable');
			const divertedElement = document.getElementById('diverted');

			if (!editableElement || !divertedElement) {
				return null;
			}

			const editableRect = editableElement.getBoundingClientRect();
			const divertedStyle = document.defaultView.getComputedStyle(divertedElement);
			const gap = parseInt(divertedStyle.gap, 10) || 0;
			const padLeft = parseInt(divertedStyle.paddingLeft, 10) || 0;
			const padRight = parseInt(divertedStyle.paddingRight, 10) || 0;
			const padTop = parseInt(divertedStyle.paddingTop, 10) || 0;
			const padBottom = parseInt(divertedStyle.paddingBottom, 10) || 0;
			const divertedRect = divertedElement.getBoundingClientRect();
			const visibleWidth = Math.max(
				1,
				divertedElement.clientWidth || 0,
				Math.round(divertedRect.width || 0),
			);
			const visibleHeight = Math.max(
				1,
				divertedElement.clientHeight || 0,
				Math.round(divertedRect.height || 0),
			);
			const scrollLeft = Math.max(0, divertedElement.scrollLeft || 0);
			const scrollTop = Math.max(0, divertedElement.scrollTop || 0);
			const editableWidth = Math.max(
				editableElement.offsetWidth || 0,
				editableRect.width || 0,
				scrollLeft + visibleWidth,
			);
			const editableHeight = Math.max(
				editableElement.offsetHeight || 0,
				editableRect.height || 0,
				scrollTop + visibleHeight,
			);

			return {
				editableRect,
				divertedRect,
				width: editableWidth,
				height: editableHeight,
				percentageReference: isPercentageLayout ? getPercentageReferenceMetrics() : null,
				gap,
				padLeft,
				padRight,
				padTop,
				padBottom,
				scrollLeft,
				scrollTop,
				gridColumnSize: defaultDivWidth + gap,
				gridRowSize: defaultDivHeight + gap,
				usableWidth: Math.max(0, editableWidth - padLeft - padRight),
				usableHeight: Math.max(0, editableHeight - padTop - padBottom),
			};
		}, [defaultDivHeight, defaultDivWidth, getPercentageReferenceMetrics, isPercentageLayout]),
		getWidgetRectPx = useCallback((widget, metrics = getEditableMetrics()) => {
			const percentageReference = isPercentageLayout
				? (metrics?.percentageReference || getPercentageReferenceMetrics())
				: null;
			const percentageSpace = isPercentageLayout
				? getPercentageCoordinateSpace(metrics, percentageReference)
				: null;
			const totalWidth = isPercentageLayout
				? percentageSpace.width
				: (metrics?.width || 0);
			const totalHeight = isPercentageLayout
				? percentageSpace.height
				: (metrics?.height || 0);
			return {
				left: isPercentageLayout
					? parseStoredPosition(widget?.left, totalWidth, percentageSpace.originLeft)
					: toPxNumber(widget?.left, 0),
				top: isPercentageLayout
					? parseStoredPosition(widget?.top, totalHeight, percentageSpace.originTop)
					: toPxNumber(widget?.top, 0),
				width: isPercentageLayout
					? parseStoredDimension(widget?.width, totalWidth, defaultWidth)
					: toPxNumber(widget?.width, defaultWidth),
				height: isPercentageLayout
					? parseStoredDimension(widget?.height, totalHeight, defaultHeight)
					: toPxNumber(widget?.height, defaultHeight),
			};
		}, [
			defaultHeight,
			defaultWidth,
			getEditableMetrics,
			getPercentageCoordinateSpace,
			getPercentageReferenceMetrics,
			isPercentageLayout,
			parseStoredDimension,
			parseStoredPosition,
			toPxNumber,
		]),
		serializeRectForStorage = useCallback((rect, metrics = getEditableMetrics()) => {
			if (!isPercentageLayout || !metrics) {
				return {
					left: rect.left,
					top: rect.top,
					width: rect.width,
					height: rect.height,
				};
			}

			const percentageReference = metrics?.percentageReference || getPercentageReferenceMetrics();
			const percentageSpace = getPercentageCoordinateSpace(metrics, percentageReference);
			const safeWidth = percentageSpace.width;
			const safeHeight = percentageSpace.height;
			const relativeLeft = Math.max(0, rect.left - percentageSpace.originLeft);
			const relativeTop = Math.max(0, rect.top - percentageSpace.originTop);

			return {
				left: formatPercentValue((relativeLeft / safeWidth) * 100),
				top: formatPercentValue((relativeTop / safeHeight) * 100),
				width: formatPercentValue((rect.width / safeWidth) * 100),
				height: formatPercentValue((rect.height / safeHeight) * 100),
			};
		}, [formatPercentValue, getEditableMetrics, getPercentageCoordinateSpace, getPercentageReferenceMetrics, isPercentageLayout]),
		storeWidgetPosition = useCallback((widget, rect, metrics = getEditableMetrics()) => ({
			...widget,
			...serializeRectForStorage(rect, metrics),
		}), [getEditableMetrics, serializeRectForStorage]),
		getWidgetsCanvasBoundsPx = useCallback((widgetsToMeasure = [], metrics = getEditableMetrics()) => {
			if (!Array.isArray(widgetsToMeasure) || widgetsToMeasure.length === 0) {
				return { maxRight: 0, maxBottom: 0 };
			}

			return widgetsToMeasure.reduce((acc, widget) => {
				const rect = getWidgetRectPx(widget, metrics);
				return {
					maxRight: Math.max(acc.maxRight, rect.left + rect.width),
					maxBottom: Math.max(acc.maxBottom, rect.top + rect.height),
				};
			}, { maxRight: 0, maxBottom: 0 });
		}, [getEditableMetrics, getWidgetRectPx]),
		getViewWidgetRectPx = useCallback((widget) => {
			const totalWidth = isPercentageLayout
				? Math.max(1, viewPercentageCanvas?.width || 0)
				: 0;
			const totalHeight = isPercentageLayout
				? Math.max(1, viewPercentageCanvas?.height || 0)
				: 0;

			return {
				left: isPercentageLayout
					? parseStoredDimension(widget?.left, totalWidth, 0)
					: toPxNumber(widget?.left, 0),
				top: isPercentageLayout
					? parseStoredDimension(widget?.top, totalHeight, 0)
					: toPxNumber(widget?.top, 0),
				width: isPercentageLayout
					? parseStoredDimension(widget?.width, totalWidth, defaultWidth)
					: toPxNumber(widget?.width, defaultWidth),
				height: isPercentageLayout
					? parseStoredDimension(widget?.height, totalHeight, defaultHeight)
					: toPxNumber(widget?.height, defaultHeight),
			};
		}, [
			defaultHeight,
			defaultWidth,
			isPercentageLayout,
			parseStoredDimension,
			toPxNumber,
			viewPercentageCanvas?.height,
			viewPercentageCanvas?.width,
		]);
	const {
		checkIsOnBackgroundImage,
		checkOverlap,
		constrainRectToBackgroundImage,
		getContainingBackgroundImage,
		getOverlappingBackgroundImage,
	} = createCollisionHandlers({
		getEditableMetrics,
		getWidgetRectPx,
		toPxNumber,
		defaultDivWidth,
		defaultDivHeight,
	});

	const findNearestEmptySpotForWidgets = (
		startLeft,
		startTop,
		widgetWidth,
		widgetHeight,
		widgetsToCheck = [],
		searchStep = null
	) => {
		const metrics = getEditableMetrics();
		return findNearestEmptySpotForMetrics({
			startLeft,
			startTop,
			widgetWidth,
			widgetHeight,
			widgetsToCheck,
			metrics,
			defaultDivWidth,
			defaultDivHeight,
			toPxNumber,
			checkOverlap,
			searchStep,
		});
	},
		findNearestEmptySpot = (startLeft, startTop, widgetWidth, widgetHeight) => {
			return findNearestEmptySpotForWidgets(
				startLeft,
				startTop,
				widgetWidth,
				widgetHeight,
				dashboard?.widgets || []
			);
		},
		getNearestGridRect = (rect, metrics = getEditableMetrics()) => getNearestGridRectValue({
			rect,
			metrics,
			defaultDivWidth,
			defaultDivHeight,
			defaultWidth,
			defaultHeight,
			toPxNumber,
		}),
		sortWidgets = sortWebDashboardWidgets,
		{
			continueRemoveBackground,
			remove,
			resetRemoveBackgroundDialog,
		} = createBackgroundRemovalHandlers({
			dashboard,
			setDashboard,
			removeBackgroundDialog,
			setRemoveBackgroundDialog,
			getEditableMetrics,
			getWidgetRectPx,
			findNearestEmptySpotForWidgets,
			storeWidgetPosition,
			sortWidgets,
			setAlertMessage,
			t,
			setAlertType,
			setShowAlert,
		}),
		duplicate = createDuplicateWidgetHandler({
			dashboard,
			setDashboard,
			getEditableMetrics,
			getWidgetRectPx,
			getMinimumWidgetSizeForType,
			defaultDivWidth,
			defaultDivHeight,
			isWidgetFreeMovementEnabled,
			toPxNumber,
			checkOverlap,
			findNearestEmptySpot,
			cloneDashboardValue,
			storeWidgetPosition,
			sortWidgets,
			setAlertMessage,
			t,
			setAlertType,
			setShowAlert,
		}),
		resizeWidget = createResizeWidgetHandler({
			dashboard,
			setDashboard,
			defaultDivWidth,
			defaultDivHeight,
			getMinimumWidgetSizeForType,
			isPercentageLayout,
			getWidgetRectPx,
			getEditableMetrics,
			isWidgetFreeMovementEnabled,
			checkIsOnBackgroundImage,
			getContainingBackgroundImage,
			getOverlappingBackgroundImage,
			canWaterWidgetsShareSpace,
			isWaterNetworkWidgetType,
			snapWaterNetworkResizeRect,
			checkOverlap,
			setAlertMessage,
			t,
			setAlertType,
			setShowAlert,
			serializeRectForStorage,
		}),
		propsFromType = (type) => getWidgetPropsFromType({
			type,
			defaultWidgetPropsByType,
			cloneDashboardValue,
		}),
		widgets = WEB_DASHBOARD_WIDGET_PALETTE.map((widgetDefinition) => {
			const widgetProps = {
				draggable: true,
				resize: false,
				resizeWidget,
				id: widgetDefinition.id,
				type: widgetDefinition.type,
				title: getWidgetPaletteTitle(widgetDefinition, t),
				width: widgetDefinition.paletteWidth || defaultWidth,
				height: widgetDefinition.paletteHeight || (widgetDefinition.heightSource === 'width' ? defaultWidth : defaultHeight),
				extraProps: widgetPalettePropsByKey[widgetDefinition.extraPropsKey],
				duplicate,
				remove,
			};

			if (widgetDefinition.includeDefaultDivSize !== false) {
				widgetProps.defaultDivHeight = defaultDivHeight;
				widgetProps.defaultDivWidth = defaultDivWidth;
			}

			return <Widget key={widgetDefinition.key} {...widgetProps} />;
		});

	const filteredWidgetOptions = filterWidgetOptionsByQuery(widgets, widgetSearchQuery);

	const prevFixedWidthRef = useRef(dashboard?.fixedWidth);
	const dragPointerOffsetRef = useRef(null);
	const selectionDragRef = useRef(null);
	const selectedGroupDragRef = useRef(null);
	const selectedGroupDragOffsetRef = useRef(null);
	const suppressSelectionClickRef = useRef(false);
	const layoutMeasureSignatureRef = useRef('');
	const fixedWidthToggleHandlerRef = useRef(null);
	const multiSelectToggleHandlerRef = useRef(null);

	const externalFixedWidthToggleHandler = useCallback((nextChecked) => {
		if (typeof fixedWidthToggleHandlerRef.current === 'function') {
			fixedWidthToggleHandlerRef.current(nextChecked);
		}
	}, []);

	const externalMultiSelectToggleHandler = useCallback((nextChecked) => {
		if (typeof multiSelectToggleHandlerRef.current === 'function') {
			multiSelectToggleHandlerRef.current(nextChecked);
		}
	}, []);

	const clearMultiSelection = useCallback(() => {
		setSelectedWidgetIds((currentIds) => (currentIds.length === 0 ? currentIds : []));
		setSelectionRect((currentRect) => (currentRect === null ? currentRect : null));
		setSelectedGroupDragOffset((currentOffset) => (currentOffset === null ? currentOffset : null));
	}, []);

	const rememberDragPointerOffset = useCallback((e) => {
		const widgetElement = e?.target?.closest?.('.widget');
		if (!widgetElement || !widgetElement.id) {
			return;
		}

		const rect = widgetElement.getBoundingClientRect();
		if (!Number.isFinite(e.clientX) || !Number.isFinite(e.clientY)) {
			return;
		}
		dragPointerOffsetRef.current = {
			id: widgetElement.id,
			offsetX: e.clientX - rect.left,
			offsetY: e.clientY - rect.top,
		};
	}, []);

	const clearDragPointerOffset = useCallback(() => {
		dragPointerOffsetRef.current = null;
	}, []);

	useEffect(() => {
		if (mode !== 'edit') {
			layoutMeasureSignatureRef.current = '';
			return undefined;
		}

		const getLayoutSignature = () => {
			const mainContainer = document.getElementById('web-dashboard-main-container');
			const dashboardElement = document.getElementById('diverted');
			const editable = document.getElementById('editable');
			const widgetsBox = document.querySelector('.widgets-box');

			return [
				mainContainer?.offsetWidth || 0,
				mainContainer?.offsetHeight || 0,
				dashboardElement?.clientWidth || 0,
				dashboardElement?.clientHeight || 0,
				editable?.offsetWidth || 0,
				editable?.offsetHeight || 0,
				widgetsBox?.offsetWidth || 0,
				widgetsBox?.offsetHeight || 0,
			].join(':');
		};

		if (typeof ResizeObserver === 'undefined' || typeof window === 'undefined') {
			const nextSignature = getLayoutSignature();
			if (nextSignature !== layoutMeasureSignatureRef.current) {
				layoutMeasureSignatureRef.current = nextSignature;
				setLayoutMeasureVersion((prev) => prev + 1);
			}
			return undefined;
		}

		let rafId = 0;
		const scheduleLayoutUpdate = () => {
			if (rafId) {
				window.cancelAnimationFrame(rafId);
			}

			rafId = window.requestAnimationFrame(() => {
				const nextSignature = getLayoutSignature();
				if (nextSignature === layoutMeasureSignatureRef.current) {
					return;
				}

				layoutMeasureSignatureRef.current = nextSignature;
				setLayoutMeasureVersion((prev) => prev + 1);
			});
		};

		const mainContainer = document.getElementById('web-dashboard-main-container');
		const dashboardElement = document.getElementById('diverted');
		const editable = document.getElementById('editable');
		const widgetsBox = document.querySelector('.widgets-box');
		const observedElements = [mainContainer, dashboardElement, editable, widgetsBox].filter(Boolean);

		const resizeObserver = new ResizeObserver(() => {
			scheduleLayoutUpdate();
		});

		observedElements.forEach((element) => resizeObserver.observe(element));
		scheduleLayoutUpdate();

		return () => {
			if (rafId) {
				window.cancelAnimationFrame(rafId);
			}
			resizeObserver.disconnect();
		};
	}, [mode]);

	const validDatastreams = getSafeDatastreamList(datastreams);
	const findDatastreamById = useCallback(findDatastreamByIdValue, []);
	const normalizeDatastreamPin = useCallback(normalizeDatastreamPinValue, []);
	const findDatastreamByReference = useCallback(findDatastreamByReferenceValue, []);

	const selectedDatastream = useMemo(() => {
		return getSelectedDatastreamForEdit({
			rawId: edit?.dataId,
			rawPin: edit?.dataPin,
			validDatastreams,
			deviceDatastream,
			findDatastreamByReference,
		});
	}, [deviceDatastream, edit?.dataId, edit?.dataPin, findDatastreamByReference, validDatastreams]);

	const getDeviceTemplateSignature = useCallback(getDeviceTemplateSignatureValue, []);

	const isStringDatastream = useMemo(
		() => isStringDatastreamValue(selectedDatastream),
		[selectedDatastream],
	);
	const isNumericDatastream = useMemo(
		() => isNumericDatastreamValue(selectedDatastream),
		[selectedDatastream],
	);

	const getDsMinMax = useCallback(getDatastreamMinMax, []);
	const getEffectiveMinMax = useCallback(getEffectiveDatastreamMinMax, []);
	const clampToMinMax = useCallback(clampValueToDatastreamMinMax, []);
	const snapValueToStep = useCallback(snapValueToDatastreamStep, []);

	const normalizeSliderValueInputChange = useCallback(normalizeSliderValueInputChangeValue, []);

	const updateWidgetLivePropsById = useCallback((widgetId, partialProps, options = {}) => {
		if (!widgetId || !partialProps || typeof partialProps !== 'object') return;

		const { syncEdit = false } = options;

		setWidgetLiveProps((prevLiveProps) => {
			const previousWidgetProps = prevLiveProps?.[widgetId] || {};
			const nextWidgetProps = { ...previousWidgetProps, ...partialProps };
			const changed = Object.keys(partialProps).some(
				(key) => previousWidgetProps?.[key] !== nextWidgetProps[key]
			);

			if (!changed) {
				return prevLiveProps;
			}

			return {
				...(prevLiveProps || {}),
				[widgetId]: nextWidgetProps,
			};
		});

		if (syncEdit) {
			setEdit((prev) => {
				if (!prev?.open || String(prev.id) !== String(widgetId)) {
					return prev;
				}

				return {
					...prev,
					props: {
						...(prev.props || {}),
						...partialProps,
					},
				};
			});
		}
	}, [setEdit]);

	const commitWidgetPropsById = useCallback((widgetId, partialProps, options = {}) => {
		if (!widgetId || !partialProps || typeof partialProps !== 'object') return;

		const { syncEdit = false } = options;
		const currentWidgets = Array.isArray(dashboard?.widgets) ? dashboard.widgets : [];

		setDashboard({
			...dashboard,
			widgets: currentWidgets.map((widget) => (
				String(widget.id) === String(widgetId)
					? {
						...widget,
						extra_props: {
							...(widget.extra_props || {}),
							...partialProps,
						},
					}
					: widget
			)),
		});

		if (syncEdit) {
			setEdit((prev) => {
				if (!prev?.open || String(prev.id) !== String(widgetId)) {
					return prev;
				}

				return {
					...prev,
					props: {
						...(prev.props || {}),
						...partialProps,
					},
				};
			});
		}
	}, [dashboard, setDashboard, setEdit]);

	const clearWidgetLivePropsById = useCallback((widgetId) => {
		if (!widgetId) return;

		setWidgetLiveProps((prevLiveProps) => {
			if (!prevLiveProps || !Object.prototype.hasOwnProperty.call(prevLiveProps, widgetId)) {
				return prevLiveProps;
			}

			const nextLiveProps = { ...prevLiveProps };
			delete nextLiveProps[widgetId];
			return nextLiveProps;
		});
	}, []);

	const stripDatastreamManagedProps = useCallback(
		(props, widgetType) => stripDatastreamManagedPropsValue(props, widgetType, cloneDashboardValue),
		[]
	);

	const remapColorStopsForDatastream = useCallback(remapColorStopsForDatastreamValue, []);

	const applyDatastreamDefaultColors = (props, widgetType, ds) => applyDatastreamDefaultColorsValue({
		props,
		widgetType,
		datastream: ds,
		themeColorKey,
	});

	const editWidget = (dashboard?.widgets || []).find((widget) => widget.id === edit.id) || null;
	const editWidgetMetrics = edit.open ? getEditableMetrics() : null;
	const editWidgetRectPx = editWidget ? getWidgetRectPx(editWidget, editWidgetMetrics || undefined) : null;

	const editContainingBackground = getEditContainingBackground({
		editWidget,
		editWidgetRectPx,
		widgets: dashboard?.widgets,
		getContainingBackgroundImage,
	});
	const editContainingBackgroundRect = editContainingBackground
		? getWidgetRectPx(editContainingBackground, editWidgetMetrics || undefined)
		: null;

	const isEditWidgetOnBackgroundImage = Boolean(editContainingBackgroundRect);

	const editWidgetLayoutTarget = edit.open && editWidget
		? {
			...editWidget,
			type: edit.type || editWidget.type,
			extra_props: edit.props || editWidget.extra_props || {},
		}
		: null;
	const editFreeMovementEnabled = isWidgetFreeMovementEnabled(editWidgetLayoutTarget);

	const showFreeGeometrySettings = Boolean(
		edit.open && editWidget && (isEditWidgetOnBackgroundImage || editFreeMovementEnabled),
	);

	const geometryOriginLeft = editContainingBackgroundRect
		? editContainingBackgroundRect.left
		: 0;
	const geometryOriginTop = editContainingBackgroundRect
		? editContainingBackgroundRect.top
		: 0;

	const geometryContainerWidth = Math.max(0, editContainingBackgroundRect?.width || editWidgetMetrics?.width || 0);
	const geometryContainerHeight = Math.max(0, editContainingBackgroundRect?.height || editWidgetMetrics?.height || 0);

	const geometryWidthValue = roundGeometryValue(Math.max(
		0.0001,
		toLengthInputNumber(
			edit.width,
			editWidgetRectPx?.width ?? defaultWidth,
		),
	));
	const geometryHeightValue = roundGeometryValue(Math.max(
		0.0001,
		toLengthInputNumber(
			edit.height,
			editWidgetRectPx?.height ?? defaultHeight,
		),
	));
	const geometryLeftValue = roundGeometryValue(Math.max(
		0,
		toLengthInputNumber(
			edit.left,
			editWidgetRectPx?.left ?? geometryOriginLeft,
		) - geometryOriginLeft,
	));
	const geometryTopValue = roundGeometryValue(Math.max(
		0,
		toLengthInputNumber(
			edit.top,
			editWidgetRectPx?.top ?? geometryOriginTop,
		) - geometryOriginTop,
	));

	const geometryRightValue = roundGeometryValue(Math.max(
		0,
		geometryContainerWidth - geometryLeftValue - geometryWidthValue,
	));
	const geometryBottomValue = roundGeometryValue(Math.max(
		0,
		geometryContainerHeight - geometryTopValue - geometryHeightValue,
	));
	const geometryUnitLabel = 'px';
	const [activeGeometryField, setActiveGeometryField] = useState(null);
	const [geometryDrafts, setGeometryDrafts] = useState({
		width: '',
		height: '',
		top: '',
		left: '',
		right: '',
		bottom: '',
	});
	const formatGeometryDraftValue = useCallback(formatGeometryDraftValueValue, []);

	useEffect(() => {
		setActiveGeometryField(null);
	}, [edit.id, edit.open]);

	useEffect(() => {
		if (!showFreeGeometrySettings) {
			setGeometryDrafts({
				width: '',
				height: '',
				top: '',
				left: '',
				right: '',
				bottom: '',
			});
			setActiveGeometryField(null);
			return;
		}

		const nextDrafts = {
			width: formatGeometryDraftValue(geometryWidthValue),
			height: formatGeometryDraftValue(geometryHeightValue),
			top: formatGeometryDraftValue(geometryTopValue),
			left: formatGeometryDraftValue(geometryLeftValue),
			right: formatGeometryDraftValue(geometryRightValue),
			bottom: formatGeometryDraftValue(geometryBottomValue),
		};

		setGeometryDrafts((prev) => ({
			width: activeGeometryField === 'width' ? prev.width : nextDrafts.width,
			height: activeGeometryField === 'height' ? prev.height : nextDrafts.height,
			top: activeGeometryField === 'top' ? prev.top : nextDrafts.top,
			left: activeGeometryField === 'left' ? prev.left : nextDrafts.left,
			right: activeGeometryField === 'right' ? prev.right : nextDrafts.right,
			bottom: activeGeometryField === 'bottom' ? prev.bottom : nextDrafts.bottom,
		}));
	}, [
		activeGeometryField,
		formatGeometryDraftValue,
		geometryBottomValue,
		geometryHeightValue,
		geometryLeftValue,
		geometryRightValue,
		geometryTopValue,
		geometryWidthValue,
		showFreeGeometrySettings,
	]);

	const {
		handleGeometryDraftChange,
		handleGeometryDraftBlur,
	} = useMemo(() => createGeometryDraftHandlers({
		formatGeometryDraftValue,
		geometryBottomValue,
		geometryContainerHeight,
		geometryContainerWidth,
		geometryHeightValue,
		geometryLeftValue,
		geometryOriginLeft,
		geometryOriginTop,
		geometryRightValue,
		geometryTopValue,
		geometryWidthValue,
		roundGeometryValue,
		setActiveGeometryField,
		setEdit,
		setGeometryDrafts,
	}), [
		formatGeometryDraftValue,
		geometryBottomValue,
		geometryContainerHeight,
		geometryContainerWidth,
		geometryHeightValue,
		geometryLeftValue,
		geometryOriginLeft,
		geometryOriginTop,
		geometryRightValue,
		geometryTopValue,
		geometryWidthValue,
	]);

	useEffect(() => {
		setIsFixedWidthMode(dashboard?.fixedWidth ?? true);
	}, [dashboard?.fixedWidth]);

	const handleFixedWidthToggle = useCallback((nextChecked) => runFixedWidthToggle({
		dashboard,
		defaultDivHeight,
		defaultDivWidth,
		defaultHeight,
		defaultWidth,
		fixedWidthResizeAlert: t.fixedWidthResizeAlert,
		fixedViewWidth,
		getEditableMetrics,
		getWidgetRectPx,
		getWidgetsCanvasBoundsPx,
		isPercentageLayout,
		lastWidgetsBoxWidth,
		nextChecked,
		setAlertMessage,
		setAlertType,
		setDashboard,
		setFixedViewWidth,
		setIsFixedWidthMode,
		setLastWidgetsBoxWidth,
		setShowAlert,
	}), [
		dashboard,
		defaultDivHeight,
		defaultDivWidth,
		defaultHeight,
		defaultWidth,
		fixedViewWidth,
		getEditableMetrics,
		getWidgetRectPx,
		getWidgetsCanvasBoundsPx,
		lastWidgetsBoxWidth,
		setDashboard,
		t.fixedWidthResizeAlert,
		isPercentageLayout,
	]);

	useEffect(() => {
		if (typeof onFixedWidthModeChange === 'function') {
			onFixedWidthModeChange(isFixedWidthMode);
		}
	}, [isFixedWidthMode, onFixedWidthModeChange]);

	useEffect(() => {
		if (mode !== 'edit' || !isPercentageLayout) {
			return undefined;
		}

		const timeout = setTimeout(() => {
			const editableElement = document.getElementById('editable');
			const referenceMetrics = getPercentageReferenceMetrics();
			const currentWidgets = dashboard?.widgets || [];
			const needsLegacyGeometryMigration = currentWidgets.some(
				(widget) => !hasStoredPercentageGeometry(widget),
			);

			if (
				!editableElement
				|| !referenceMetrics
				|| currentWidgets.length === 0
				|| !needsLegacyGeometryMigration
			) {
				return;
			}

			const editableRect = editableElement.getBoundingClientRect();
			const safeWidth = Math.max(1, referenceMetrics.width);
			const safeHeight = Math.max(1, referenceMetrics.height);

			let changed = false;
			const normalizedWidgets = currentWidgets.map((widget) => {
				if (hasStoredPercentageGeometry(widget)) {
					return widget;
				}

				const widgetElement = document.getElementById(widget?.id);
				if (!widgetElement) {
					return widget;
				}

				const rect = widgetElement.getBoundingClientRect();
				const nextGeometry = {
					left: formatPercentValue(((rect.left - editableRect.left) / safeWidth) * 100),
					top: formatPercentValue(((rect.top - editableRect.top) / safeHeight) * 100),
					width: formatPercentValue((rect.width / safeWidth) * 100),
					height: formatPercentValue((rect.height / safeHeight) * 100),
				};

				if (
					widget.left !== nextGeometry.left ||
					widget.top !== nextGeometry.top ||
					widget.width !== nextGeometry.width ||
					widget.height !== nextGeometry.height
				) {
					changed = true;
					return {
						...widget,
						...nextGeometry,
					};
				}

				return widget;
			});

			if (!changed) {
				return;
			}

			setDashboard({
				...dashboard,
				widgets: sortWidgets(normalizedWidgets),
			});
		}, 0);

		return () => clearTimeout(timeout);
	}, [
		mode,
		isPercentageLayout,
		dashboard,
		dashboard?.widgets,
		layoutMeasureVersion,
		dims.width,
		dims.height,
		formatPercentValue,
		hasStoredPercentageGeometry,
		getPercentageReferenceMetrics,
		sortWidgets,
		setDashboard,
	]);

	useEffect(() => {
		fixedWidthToggleHandlerRef.current = handleFixedWidthToggle;
	}, [handleFixedWidthToggle]);

	useEffect(() => {
		if (typeof onFixedWidthToggleHandlerReady !== 'function') {
			return undefined;
		}

		onFixedWidthToggleHandlerReady(externalFixedWidthToggleHandler);
		return () => {
			onFixedWidthToggleHandlerReady(null);
		};
	}, [externalFixedWidthToggleHandler, onFixedWidthToggleHandlerReady]);

	useEffect(() => {
		if (mode !== 'edit') {
			setFixedWidthGuide((prev) => (prev === null ? prev : null));
			return;
		}
		if (isFixedWidthMode) {
			setFixedWidthGuide((prev) => (prev === null ? prev : null));
			return;
		}

		const mainContainer = document.getElementById('web-dashboard-main-container');
		const dashboardElement = document.getElementById('diverted');
		const editable = document.getElementById('editable');
		const widgetsBox = document.querySelector('.widgets-box');

		if (!mainContainer || !dashboardElement || !editable || !widgetsBox) {
			setFixedWidthGuide((prev) => (prev === null ? prev : null));
			return;
		}

		const style = document.defaultView.getComputedStyle(dashboardElement);
		const gap = parseInt(style.gap) || 0;

		const editableStyle = document.defaultView.getComputedStyle(editable);
		const paddingLeft = parseInt(editableStyle.paddingLeft) || 0;
		const paddingRight = parseInt(editableStyle.paddingRight) || 0;
		const totalHorizontalPadding = paddingLeft + paddingRight;

		const mainContainerGap = 8;
		const widgetsBoxActualWidth = widgetsBox ? widgetsBox.offsetWidth : 0;
		const rememberedWidgetsBoxWidth = widgetsBoxActualWidth + mainContainerGap;
		if (rememberedWidgetsBoxWidth !== lastWidgetsBoxWidth) setLastWidgetsBoxWidth(rememberedWidgetsBoxWidth);
		const rememberedTotalWidth = mainContainer.offsetWidth - mainContainerGap;
		if (rememberedTotalWidth !== fixedViewWidth) setFixedViewWidth(rememberedTotalWidth);
		const actualMainContainerWidth = mainContainer.offsetWidth;
		const availableWidthForDiverted = Math.max(
			1,
			actualMainContainerWidth - widgetsBoxActualWidth - mainContainerGap
		);
		let fixedWidthPx;
		if (isPercentageLayout) {
			fixedWidthPx = availableWidthForDiverted;
		} else {
			const minGridColsForDefaultWidget = Math.ceil(defaultWidth / (defaultDivWidth + gap));
			let calculatedColumnsForFixed = Math.max(
				1,
				minGridColsForDefaultWidget,
				Math.floor((availableWidthForDiverted - totalHorizontalPadding) / (defaultDivWidth + gap))
			);
			calculatedColumnsForFixed = Math.max(1, calculatedColumnsForFixed);

			fixedWidthPx = calculatedColumnsForFixed * (defaultDivWidth + gap) + totalHorizontalPadding;
			fixedWidthPx = Math.max(fixedWidthPx, defaultWidth + totalHorizontalPadding);
			const fixedCanvasTargetWidth = availableWidthForDiverted;
			fixedWidthPx = Math.max(fixedWidthPx, fixedCanvasTargetWidth);
		}

		const fixedHeightPx = getElementViewportHeight(dashboardElement) || editable.offsetHeight || 0;

		if (!Number.isFinite(fixedWidthPx) || fixedWidthPx <= 0) {
			setFixedWidthGuide((prev) => (prev === null ? prev : null));
			return;
		}

		const nextGuide = { width: fixedWidthPx, height: fixedHeightPx };
		setFixedWidthGuide((prev) => (sameMeasuredSize(prev, nextGuide) ? prev : nextGuide));
	}, [
		mode,
		isFixedWidthMode,
		dims.width,
		dims.height,
		defaultDivWidth,
		defaultDivHeight,
		defaultWidth,
		defaultHeight,
		dashboard?.widgets,
		fixedViewWidth,
		isPercentageLayout,
		lastWidgetsBoxWidth,
		layoutMeasureVersion,
	]);

	useEffect(() => {
		if (mode === 'edit' || !isPercentageLayout) {
			setViewPercentageCanvas((prev) => {
				if ((prev?.width || 0) === 0 && (prev?.height || 0) === 0) return prev;
				return { width: 0, height: 0 };
			});
			return;
		}

		const timeout = setTimeout(() => {
			const dashboardElement = document.getElementById('diverted-view');
			if (!dashboardElement) return;

			const viewportWidth = Math.max(1, dashboardElement.clientWidth);
			const viewportHeight = Math.max(1, dashboardElement.clientHeight);

			const nextWidth = viewportWidth;
			const nextHeight = viewportHeight;

			setViewPercentageCanvas((prev) => {
				const prevWidth = prev?.width || 0;
				const prevHeight = prev?.height || 0;
				if (
					Math.abs(prevWidth - nextWidth) < 1 &&
					Math.abs(prevHeight - nextHeight) < 1
				) {
					return prev;
				}
				return { width: nextWidth, height: nextHeight };
			});
		}, 0);

		return () => clearTimeout(timeout);
	}, [
		mode,
		isPercentageLayout,
		dashboard?.widgets,
		isFixedWidthMode,
		fixedViewWidth,
		defaultDivWidth,
		defaultDivHeight,
		defaultWidth,
		defaultHeight,
		dims.width,
		dims.height,
		getWidgetsCanvasBoundsPx,
	]);

	const editableWaterNetworkState = useMemo(() => {
		if (mode !== 'edit') {
			return null;
		}

		const overlayMetricsVersion = layoutMeasureVersion + lastWidgetsBoxWidth + (isPercentageLayout ? 1 : 0);
		const metrics = getEditableMetrics();
		if (!metrics || overlayMetricsVersion < 0) {
			return null;
		}

		const connections = buildWaterNetworkConnections(
			dashboard?.widgets || [],
			(widget) => getWidgetRectPx(widget, metrics),
			metrics,
		);

		return {
			width: metrics.width,
			height: metrics.height,
			connections,
		};
	}, [
		mode,
		dashboard?.widgets,
		getEditableMetrics,
		getWidgetRectPx,
		layoutMeasureVersion,
		lastWidgetsBoxWidth,
		isPercentageLayout,
	]);

	const viewWaterNetworkState = useMemo(() => {
		if (mode === 'edit') {
			return null;
		}

		const widgetsToMeasure = dashboard?.widgets || [];
		if (widgetsToMeasure.length === 0) {
			return null;
		}

		const canvasBounds = widgetsToMeasure.reduce((acc, widget) => {
			const rect = getViewWidgetRectPx(widget);
			return {
				maxRight: Math.max(acc.maxRight, rect.left + rect.width),
				maxBottom: Math.max(acc.maxBottom, rect.top + rect.height),
			};
		}, {
			maxRight: isPercentageLayout ? (viewPercentageCanvas?.width || 0) : 0,
			maxBottom: isPercentageLayout ? (viewPercentageCanvas?.height || 0) : 0,
		});

		const connections = buildWaterNetworkConnections(
			widgetsToMeasure,
			getViewWidgetRectPx,
			null,
		);

		return {
			width: canvasBounds.maxRight,
			height: canvasBounds.maxBottom,
			connections,
		};
	}, [
		mode,
		dashboard?.widgets,
		getViewWidgetRectPx,
		isPercentageLayout,
		viewPercentageCanvas?.height,
		viewPercentageCanvas?.width,
	]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (mode === 'edit') {
				const mainContainer = document.getElementById('web-dashboard-main-container');
				const dashboardElement = document.getElementById('diverted');
				const editable = document.getElementById('editable');
				const widgetsBox = document.querySelector('.widgets-box');

				if (!mainContainer || !dashboardElement || !editable) return;

				const style = document.defaultView.getComputedStyle(dashboardElement);
				const gap = parseInt(style.gap) || 0;
				const editableStyle = document.defaultView.getComputedStyle(editable);
				const paddingLeft = parseInt(editableStyle.paddingLeft) || 0;
				const paddingRight = parseInt(editableStyle.paddingRight) || 0;
				const paddingTop = parseInt(editableStyle.paddingTop) || 0;
				const paddingBottom = parseInt(editableStyle.paddingBottom) || 0;
				const totalHorizontalPadding = paddingLeft + paddingRight;
				const totalVerticalPadding = paddingTop + paddingBottom;
				const gridColumnUnit = Math.max(1, defaultDivWidth + gap);
				const gridRowUnit = Math.max(1, defaultDivHeight + gap);

				let calculatedColumns;
				let calculatedRows;
				let currentEditableWidthPx;
				let currentEditableHeightPx;

				const minGridColsForDefaultWidget = Math.ceil(defaultWidth / (defaultDivWidth + gap));
				const minGridRowsForDefaultWidget = Math.ceil(defaultHeight / (defaultDivHeight + gap));

				const mainContainerGap = 8;
				const widgetsBoxActualWidth = widgetsBox ? widgetsBox.offsetWidth : 0;
				const actualMainContainerWidth = mainContainer.offsetWidth;
				const availableWidthForDiverted = Math.max(
					1,
					actualMainContainerWidth - widgetsBoxActualWidth - mainContainerGap,
				);
				const availableHeightForDiverted = getElementViewportHeight(mainContainer);
				const fixedCanvasTargetWidth = availableWidthForDiverted;
				const {
					maxRight: maxWidgetRight,
					maxBottom: maxWidgetBottom,
				} = getWidgetsCanvasBoundsPx(dashboard?.widgets || []);
				const percentageVerticalBuffer = getCanvasBottomBufferPx({
					viewportHeight: availableHeightForDiverted,
					defaultHeight,
					rowUnit: gridRowUnit,
				});
				const percentageMinimumVisibleHeight = Math.max(1, availableHeightForDiverted);
				let dashboardViewportHeightPx = availableHeightForDiverted;

				if (isFixedWidthMode) {
					if (isPercentageLayout) {
						calculatedColumns = 0;
						calculatedRows = 0;

						currentEditableWidthPx = Math.max(1, availableWidthForDiverted);
						currentEditableHeightPx = Math.max(
							percentageMinimumVisibleHeight,
							defaultHeight + totalVerticalPadding,
							maxWidgetBottom + totalVerticalPadding + gap + percentageVerticalBuffer,
						);
						dashboardViewportHeightPx = percentageMinimumVisibleHeight;

						dashboardElement.style.overflowX = 'hidden';
						dashboardElement.style.overflowY = 'auto';
						setElementPixelStyle(dashboardElement, 'width', availableWidthForDiverted);
						setElementPixelStyle(dashboardElement, 'height', dashboardViewportHeightPx, 'important');
						dashboardElement.style.flexGrow = 1;
						dashboardElement.style.flexShrink = 1;
						dashboardElement.style.justifyContent = 'flex-start';
						dashboardElement.style.alignContent = 'flex-start';
					} else {
						calculatedColumns = Math.max(
							1,
							minGridColsForDefaultWidget,
							Math.floor((availableWidthForDiverted - totalHorizontalPadding) / (defaultDivWidth + gap))
						);

						calculatedColumns = Math.max(1, calculatedColumns);

						currentEditableWidthPx = calculatedColumns * (defaultDivWidth + gap) + totalHorizontalPadding;
						currentEditableWidthPx = Math.max(currentEditableWidthPx, defaultWidth + totalHorizontalPadding);
						currentEditableWidthPx = Math.max(currentEditableWidthPx, fixedCanvasTargetWidth);

						calculatedColumns = Math.max(
							1,
							minGridColsForDefaultWidget,
							Math.floor((currentEditableWidthPx - totalHorizontalPadding + gap) / (defaultDivWidth + gap))
						);
						calculatedColumns = Math.max(1, calculatedColumns);
						dashboardElement.style.overflowX = 'hidden';
						dashboardElement.style.overflowY = 'auto';

						const { maxBottom: maxWidgetBottom } = getWidgetsCanvasBoundsPx(dashboard?.widgets || []);
						calculatedRows = Math.max(
							minGridRowsForDefaultWidget,
							Math.floor((availableHeightForDiverted - totalVerticalPadding) / gridRowUnit),
							Math.ceil((maxWidgetBottom + gap) / gridRowUnit) + EDITABLE_TRAILING_ROWS
						);
						calculatedRows = Math.max(1, calculatedRows);

						currentEditableHeightPx = calculatedRows * gridRowUnit + totalVerticalPadding;
						currentEditableHeightPx = Math.max(
							currentEditableHeightPx,
							defaultHeight + totalVerticalPadding,
						);
						dashboardViewportHeightPx = Math.max(
							1,
							Math.min(currentEditableHeightPx, availableHeightForDiverted),
						);

						setElementPixelStyle(dashboardElement, 'height', dashboardViewportHeightPx, 'important');
						setElementPixelStyle(dashboardElement, 'width', availableWidthForDiverted);
						dashboardElement.style.flexGrow = 1;
						dashboardElement.style.flexShrink = 1;
						dashboardElement.style.justifyContent = 'flex-start';
						dashboardElement.style.alignContent = 'flex-start';
					}
				} else {
					dashboardElement.style.removeProperty('flex-grow');
					dashboardElement.style.removeProperty('flex-shrink');
					dashboardElement.style.removeProperty('justify-content');
					dashboardElement.style.removeProperty('align-content');

					const viewportWidth = availableWidthForDiverted;
					const viewportHeight = availableHeightForDiverted;

					if (isPercentageLayout) {
						calculatedColumns = 0;
						calculatedRows = 0;

						const percentageViewportWidth = Math.max(1, availableWidthForDiverted);

						currentEditableWidthPx = percentageViewportWidth;
						currentEditableHeightPx = Math.max(
							percentageMinimumVisibleHeight,
							defaultHeight + totalVerticalPadding,
							maxWidgetBottom + totalVerticalPadding + gap + percentageVerticalBuffer,
						);
						dashboardViewportHeightPx = percentageMinimumVisibleHeight;

						dashboardElement.style.overflowX = 'hidden';
						dashboardElement.style.overflowY = 'auto';
						setElementPixelStyle(dashboardElement, 'width', percentageViewportWidth);
						setElementPixelStyle(dashboardElement, 'height', dashboardViewportHeightPx, 'important');
						dashboardElement.style.flexGrow = 1;
						dashboardElement.style.flexShrink = 1;
						dashboardElement.style.justifyContent = 'flex-start';
						dashboardElement.style.alignContent = 'flex-start';
					} else {
						calculatedColumns = Math.max(
							1,
							minGridColsForDefaultWidget,
							Math.floor((viewportWidth - totalHorizontalPadding) / gridColumnUnit),
							Math.ceil(maxWidgetRight / gridColumnUnit) + EDITABLE_TRAILING_ROWS
						);
						calculatedColumns = Math.max(1, calculatedColumns);

						calculatedRows = Math.max(
							1,
							minGridRowsForDefaultWidget,
							Math.floor((viewportHeight - totalVerticalPadding) / gridRowUnit),
							Math.ceil(maxWidgetBottom / gridRowUnit) + EDITABLE_TRAILING_ROWS
						);
						calculatedRows = Math.max(1, calculatedRows);

						currentEditableWidthPx = calculatedColumns * gridColumnUnit + totalHorizontalPadding;
						currentEditableWidthPx = Math.max(currentEditableWidthPx, maxWidgetRight + totalHorizontalPadding + gap);
						currentEditableWidthPx = Math.max(currentEditableWidthPx, defaultWidth + totalHorizontalPadding);

						currentEditableHeightPx = calculatedRows * gridRowUnit + totalVerticalPadding;
						currentEditableHeightPx = Math.max(currentEditableHeightPx, maxWidgetBottom + totalVerticalPadding + gap);
						currentEditableHeightPx = Math.max(currentEditableHeightPx, defaultHeight + totalVerticalPadding);
						dashboardViewportHeightPx = Math.max(
							1,
							Math.min(currentEditableHeightPx, availableHeightForDiverted),
						);

						dashboardElement.style.overflowX = 'auto';
						dashboardElement.style.overflowY = 'auto';
						setElementPixelStyle(dashboardElement, 'width', availableWidthForDiverted);
						setElementPixelStyle(dashboardElement, 'height', dashboardViewportHeightPx, 'important');
					}
				}

				if (isPercentageLayout) {
					const guideColumnUnit = Math.max(1, defaultDivWidth + gap);
					const guideRowUnit = Math.max(1, defaultDivHeight + gap);
					const renderedGuideHeight = Math.max(
						dashboardViewportHeightPx || 0,
						currentEditableHeightPx || 0,
					);
					const guideCanvasWidth = Math.max(
						1,
						currentEditableWidthPx || dashboardElement?.clientWidth || editable?.offsetWidth || availableWidthForDiverted,
					);
					const guideCanvasHeight = Math.max(
						defaultHeight + totalVerticalPadding,
						renderedGuideHeight,
					);

					calculatedColumns = Math.max(
						1,
						minGridColsForDefaultWidget,
						Math.ceil((guideCanvasWidth - totalHorizontalPadding + gap) / guideColumnUnit),
					);
					calculatedRows = Math.max(
						1,
						minGridRowsForDefaultWidget,
						Math.ceil((guideCanvasHeight - totalVerticalPadding + gap) / guideRowUnit),
					);
				}

				const guideViewportWidth = Math.max(
					1,
					dashboardElement?.clientWidth || availableWidthForDiverted,
				);
				const guideViewportOuterWidth = Math.max(
					guideViewportWidth,
					Math.round(dashboardElement?.getBoundingClientRect?.().width || 0),
					dashboardElement?.offsetWidth || 0,
				);
				const guideViewportHeight = Math.max(
					1,
					getElementViewportHeight(dashboardElement),
					dashboardViewportHeightPx || 0,
					availableHeightForDiverted,
				);
				const guideScrollLeft = Math.max(0, dashboardElement?.scrollLeft || 0);
				const guideScrollTop = Math.max(0, dashboardElement?.scrollTop || 0);
				const isFixedPixelLayout = isFixedWidthMode && !isPercentageLayout;
				const guideHorizontalBuffer = Math.max(
					gap,
					guideViewportOuterWidth - guideViewportWidth,
				);
				const guideCoverageWidth = isFixedPixelLayout
					? Math.max(1, availableWidthForDiverted)
					: Math.max(
						guideScrollLeft + guideViewportOuterWidth + guideHorizontalBuffer,
						currentEditableWidthPx || 0,
						availableWidthForDiverted,
					);
				const guideCoverageHeight = Math.max(
					guideScrollTop + guideViewportHeight,
					currentEditableHeightPx || 0,
					dashboardViewportHeightPx || 0,
					availableHeightForDiverted,
				);
				const visualGuideColumns = isFixedPixelLayout
					? Math.max(
						1,
						Math.floor((guideCoverageWidth - totalHorizontalPadding + gap) / gridColumnUnit),
					)
					: Math.max(
						1,
						Math.ceil((guideCoverageWidth - totalHorizontalPadding + gap) / gridColumnUnit) + 1,
					);
				const visualGuideRows = Math.max(
					1,
					Math.ceil((guideCoverageHeight - totalVerticalPadding + gap) / gridRowUnit),
				);

				if (dashboardElement && visualGuideColumns > 0) {
					dashboardElement.style.gridTemplateColumns = `repeat(${visualGuideColumns}, ${defaultDivWidth}px)`;
					dashboardElement.style.gridAutoRows = `${defaultDivHeight}px`;
				} else if (dashboardElement) {
					dashboardElement.style.removeProperty('grid-template-columns');
					dashboardElement.style.removeProperty('grid-auto-rows');
				}

				// Keep visual diversion guides, but cap node count to avoid render lag.
				const maxDiversionNodes = 12000;
				const safeColumns = Math.max(1, visualGuideColumns);
				const maxRowsByCap = Math.max(1, Math.floor(maxDiversionNodes / safeColumns));
				const rowsForGuides = Math.max(1, Math.min(visualGuideRows, maxRowsByCap));
				const totalGuides = Math.min(maxDiversionNodes, safeColumns * rowsForGuides);

				const diversions = [];
				for (let i = 0; i < totalGuides; i++) {
					diversions.push(
						<div
							key={`diversion-${i}`}
							className='divertion'
							style={{
								width: defaultDivWidth,
								height: defaultDivHeight,
								borderRadius: '9px',
								backgroundColor: 'var(--diveration-bg)'
							}}
						/>
					);
				}

				if (editable) {
					editable.style.width = `${currentEditableWidthPx}px`;
					editable.style.height = `${currentEditableHeightPx}px`;
				}

				setDivs((prev) => {
					const prevWidth = prev?.[0]?.props?.style?.width;
					const prevHeight = prev?.[0]?.props?.style?.height;
					if (
						prev.length === diversions.length &&
						prevWidth === defaultDivWidth &&
						prevHeight === defaultDivHeight
					) {
						return prev;
					}
					return diversions;
				});
			} else {
				setDivs((prev) => (prev.length === 0 ? prev : []));
			}
			prevFixedWidthRef.current = dashboard?.fixedWidth;

		}, 0);

		return () => clearTimeout(timeout);
	}, [
		dims.height,
		dims.width,
		mode,
		isFixedWidthMode,
		dashboard?.fixedWidth,
		dashboard?.widgets,
		defaultDivWidth,
		defaultDivHeight,
		defaultWidth,
		defaultHeight,
		fixedViewWidth,
		getWidgetsCanvasBoundsPx,
		isPercentageLayout,
		lastWidgetsBoxWidth,
		layoutMeasureVersion,
	]);

	const legacyEnvironmentalWidgetWidth = defaultWidth * 2;
	const legacyEnvironmentalWidgetHeight = defaultHeight * 2;
	const normalizedEnvironmentalWidgetWidth = getSpannedWidgetSize(defaultWidth, 2);
	const normalizedEnvironmentalWidgetHeight = getSpannedWidgetSize(defaultHeight, 2);

	useEffect(() => {
		if (!dashboard || !Array.isArray(dashboard.widgets) || typeof setDashboard !== 'function') {
			return;
		}

		const normalizedResult = normalizeLegacyWidgetDimensions({
			widgets: dashboard.widgets,
			widgetType: ENVIRONMENTAL_STATUS_WIDGET_TYPE,
			legacyWidth: legacyEnvironmentalWidgetWidth,
			legacyHeight: legacyEnvironmentalWidgetHeight,
			normalizedWidth: normalizedEnvironmentalWidgetWidth,
			normalizedHeight: normalizedEnvironmentalWidgetHeight,
		});

		if (!normalizedResult.changed) {
			return;
		}

		setDashboard({
			...dashboard,
			widgets: normalizedResult.widgets,
		});
	}, [
		dashboard,
		legacyEnvironmentalWidgetHeight,
		legacyEnvironmentalWidgetWidth,
		normalizedEnvironmentalWidgetHeight,
		normalizedEnvironmentalWidgetWidth,
		setDashboard,
	]);

	useEffect(() => {
		if (mode !== 'edit' || !multiSelectEnabled) {
			clearMultiSelection();
		}
	}, [clearMultiSelection, mode, multiSelectEnabled]);

	useEffect(() => {
		if (typeof onMultiSelectStateChange === 'function') {
			onMultiSelectStateChange(multiSelectEnabled);
		}
	}, [multiSelectEnabled, onMultiSelectStateChange]);

	useEffect(() => {
		const availableIds = new Set((dashboard?.widgets || []).map((widget) => String(widget.id)));
		setSelectedWidgetIds((currentIds) => {
			const filteredIds = currentIds.filter((id) => availableIds.has(String(id)));
			return filteredIds.length === currentIds.length ? currentIds : filteredIds;
		});
	}, [dashboard?.widgets]);

	const selectedWidgetIdSet = useMemo(
		() => getSelectedWidgetIdSet(selectedWidgetIds || []),
		[selectedWidgetIds],
	);

	const selectedWidgetBounds = useMemo(() => {
		if (mode !== 'edit' || !multiSelectEnabled || selectedWidgetIds.length === 0) {
			return null;
		}

		const metrics = getEditableMetrics();
		if (!metrics) return null;

		const selectedRects = getSelectedWidgetRectEntries({
			widgets: dashboard?.widgets || [],
			selectedIdSet: selectedWidgetIdSet,
			getWidgetRectPx,
			metrics,
		});

		return getSelectedGroupBounds(selectedRects);
	}, [
		dashboard?.widgets,
		getEditableMetrics,
		getWidgetRectPx,
		mode,
		multiSelectEnabled,
		selectedWidgetIdSet,
		selectedWidgetIds.length,
	]);

	const handleMultiSelectToggle = useCallback((checked) => {
		setMultiSelectEnabled((currentValue) => (currentValue === checked ? currentValue : checked));
		if (!checked) {
			clearMultiSelection();
		}
	}, [clearMultiSelection]);

	useEffect(() => {
		multiSelectToggleHandlerRef.current = handleMultiSelectToggle;
	}, [handleMultiSelectToggle]);

	useEffect(() => {
		if (typeof onMultiSelectToggleHandlerReady !== 'function') {
			return undefined;
		}

		onMultiSelectToggleHandlerReady(externalMultiSelectToggleHandler);
		return () => {
			onMultiSelectToggleHandlerReady(null);
		};
	}, [externalMultiSelectToggleHandler, onMultiSelectToggleHandlerReady]);

	const getSelectedGroupMoveContext = useCallback(() => {
		const metrics = getEditableMetrics();
		const divertedElement = document.getElementById('diverted');
		if (!metrics || !divertedElement || selectedWidgetIds.length === 0) return null;

		const moveMetrics = getSelectedGroupMoveMetrics({
			divertedElement,
			metrics,
		});
		const selectedIdSet = getSelectedWidgetIdSet(selectedWidgetIds);
		const selectedRects = getSelectedWidgetRectEntries({
			widgets: dashboard?.widgets || [],
			selectedIdSet,
			getWidgetRectPx,
			metrics,
		});
		const bounds = getSelectedGroupBounds(selectedRects);
		if (!bounds) return null;
		const gap = metrics?.gap || 0;
		const allSelectedWidgetsFreeMovement = (
			selectedRects.length > 0 &&
			selectedRects.every(({ widget }) => isWidgetFreeMovementEnabled(widget))
		);

		return {
			metrics,
			...moveMetrics,
			selectedIdSet,
			selectedRects,
			bounds,
			allSelectedWidgetsFreeMovement,
			gridColumnSize: Math.max(1, defaultDivWidth + gap),
			gridRowSize: Math.max(1, defaultDivHeight + gap),
			freeformSearchStep: WATER_NETWORK_FREEFORM_SEARCH_STEP,
		};
	}, [dashboard?.widgets, defaultDivHeight, defaultDivWidth, getEditableMetrics, getWidgetRectPx, selectedWidgetIds]);

	const clampSelectedGroupDelta = useCallback((deltaX, deltaY, context = getSelectedGroupMoveContext()) => {
		if (!context) return { x: 0, y: 0 };

		const snappedDelta = context.allSelectedWidgetsFreeMovement
			? { x: deltaX, y: deltaY }
			: {
				x: Math.round(deltaX / context.gridColumnSize) * context.gridColumnSize,
				y: Math.round(deltaY / context.gridRowSize) * context.gridRowSize,
			};

		return getClampedSelectedGroupDelta({
			deltaX: snappedDelta.x,
			deltaY: snappedDelta.y,
			bounds: context.bounds,
			padLeft: context.padLeft,
			padRight: context.padRight,
			padTop: context.padTop,
			padBottom: context.padBottom,
			effectiveWidth: context.effectiveWidth,
			effectiveHeight: context.effectiveHeight,
		});
	}, [getSelectedGroupMoveContext]);

	const {
		duplicateSelectedGroup,
		getSelectedGroupFreeMovementState,
		handleEditableClickCapture,
		handleEditablePointerDown,
		removeSelectedGroup,
		startSelectedGroupDrag,
		toggleSelectedGroupFreeMovement,
	} = createSelectionHandlers({
		mode,
		multiSelectEnabled,
		selectedWidgetIds,
		selectedWidgetIdSet,
		dashboard,
		getEditableMetrics,
		getWidgetRectPx,
		getSelectedGroupMoveContext,
		clampSelectedGroupDelta,
		selectedGroupDragRef,
		selectedGroupDragOffsetRef,
		selectionDragRef,
		suppressSelectionClickRef,
		setSelectedGroupDragOffset,
		setSelectionRect,
		setSelectedWidgetIds,
		constrainRectToBackgroundImage,
		checkOverlap,
		setAlertMessage,
		t,
		setAlertType,
		setShowAlert,
		storeWidgetPosition,
		setDashboard,
		sortWidgets,
		findNearestEmptySpotForWidgets,
		cloneDashboardValue,
		isWidgetFreeMovementEnabled,
	});
	const selectedGroupFreeMovementEnabled = useMemo(
		() => getSelectedGroupFreeMovementState(),
		[getSelectedGroupFreeMovementState],
	);
	const handleDashboardDragOver = useCallback((e) => {
		e.preventDefault();
		if (e.dataTransfer) {
			e.dataTransfer.dropEffect = 'move';
		}
	}, []);

	const drop = createDropHandler({
		dashboard,
		setDashboard,
		devices,
		mode,
		multiSelectEnabled,
		selectedWidgetIdSet,
		selectedWidgetIds,
		defaultWidth,
		defaultHeight,
		defaultDivWidth,
		defaultDivHeight,
		getDefaultWidgetDimensions,
		getWidgetRectPx,
		getEditableMetrics,
		getMinimumWidgetSizeForType,
		propsFromType,
		dragPointerOffsetRef,
		getDropContainerStyleMetrics,
		getDropWidgetSize,
		getDropBounds,
		getRelativeDropPoint,
		isWidgetFreeMovementEnabled,
		getInitialDropPoint,
		shouldSnapWaterNetworkDropPoint,
		isWaterNetworkWidgetType,
		isWaterNetworkLinkEnabled,
		getSnappedWaterNetworkDropPoint,
		snapWaterNetworkMoveRect,
		isDropCandidateWithinBounds,
		checkOverlap,
		isImmediateDropAllowedWidget,
		getDropSearchStep,
		WATER_NETWORK_FREEFORM_SEARCH_STEP,
		getDropSearchGridSize,
		getNearestAvailableDropCandidate,
		shouldShowNoDropSpaceAlert,
		setAlertMessage,
		t,
		setAlertType,
		setShowAlert,
		getSelectedWidgetIdSet,
		getSelectedWidgetRectEntries,
		getSelectedGroupBounds,
		getClampedSelectedGroupDelta,
		getSelectedGroupMovedRects,
		constrainRectToBackgroundImage,
		storeWidgetPosition,
		sortWidgets,
		getConstrainedOrFallbackRect,
		isInvalidWidgetOverlap,
		buildNewDashboardWidget,
		getDefaultWidgetTitle,
	});
	const removeBackgroundDialogMessage = getRemoveBackgroundDialogMessage({
		removeBackgroundDialog,
		t,
	});

	const {
		handleCancelWidgetSettings,
		handleSaveWidgetSettings,
	} = createSettingsHandlers({
		setEdit,
		saveWidgetSettingsArgs: {
			dashboard,
			devices,
			validDatastreams,
			findDatastreamByReference,
			normalizeDatastreamPin,
			edit,
			sanitizeWidgetFontSizeProps,
			cloneDashboardValue,
			stripDatastreamManagedProps,
			showFreeGeometrySettings,
			getWidgetRectPx,
			isPercentageLayout,
			geometryWidthValue,
			geometryHeightValue,
			geometryLeftValue,
			geometryTopValue,
			geometryOriginLeft,
			geometryOriginTop,
			roundGeometryValue,
			toLengthInputNumber,
			constrainRectToBackgroundImage,
			isWidgetFreeMovementEnabled,
			isWaterNetworkWidgetType,
			snapWaterNetworkMoveRect,
			checkOverlap,
			getNearestGridRect,
			findNearestEmptySpotForWidgets,
			storeWidgetPosition,
			setDashboard,
			clearWidgetLivePropsById,
			setAlertMessage,
			t,
			setAlertType,
			setShowAlert,
			setEdit,
			getEditableMetrics,
			serializeRectForStorage,
		},
	});
	return (
		<>
			<WebDashboardEditToggles
				mode={mode}
				t={t}
				isFixedWidthMode={isFixedWidthMode}
				multiSelectEnabled={multiSelectEnabled}
				externalFixedWidthControl={externalFixedWidthControl}
				externalMultiSelectControl={externalMultiSelectControl}
				handleFixedWidthToggle={handleFixedWidthToggle}
				handleMultiSelectToggle={handleMultiSelectToggle}
			/>

			<WebDashboardMainContainer
				maxWidth={maxWidth}
				maxHeight={maxHeight}
				rememberDragPointerOffset={rememberDragPointerOffset}
				clearDragPointerOffset={clearDragPointerOffset}
			>

				<WidgetSettingsModal edit={edit} t={t}>
					<WidgetSettingsContent>
						<WidgetDatastreamEditor
							addDatastream={addDatastream}
							setAddDatastream={setAddDatastream}
							datastreams={datastreams}
							setDatastreams={setDatastreams}
							validDatastreams={validDatastreams}
							getNextDatastreamId={getNextDatastreamId}
							templateEvaluationData={templateEvaluationData}
							setTemplateEvaluationData={setTemplateEvaluationData}
						/>
						<WidgetSettingsEditorPanel
							edit={edit}
							setEdit={setEdit}
							t={t}
							editFreeMovementEnabled={editFreeMovementEnabled}
							fontSizeFields={fontSizeFields}
							handleFontSizeBlur={handleFontSizeBlur}
							handleFontSizeChange={handleFontSizeChange}
							showFreeGeometrySettings={showFreeGeometrySettings}
							geometryUnitLabel={geometryUnitLabel}
							geometryDrafts={geometryDrafts}
							setActiveGeometryField={setActiveGeometryField}
							handleGeometryDraftBlur={handleGeometryDraftBlur}
							handleGeometryDraftChange={handleGeometryDraftChange}
							hasColorSettings={hasColorSettings}
							colorThemeMode={colorThemeMode}
							chartTypes={chartTypes}
							aggregations={aggregations}
							devices={devices}
							validDatastreams={validDatastreams}
							deviceSelectOptions={deviceSelectOptions}
							setAddDatastream={setAddDatastream}
							isHiddenWidgetEditorPropKey={isHiddenWidgetEditorPropKey}
							themeColorKey={themeColorKey}
							themeColorValue={themeColorValue}
							alignments={alignments}
							deviceDatastream={deviceDatastream}
							setDeviceDatastream={setDeviceDatastream}
							selectedDatastream={selectedDatastream}
							findDatastreamById={findDatastreamById}
							stripDatastreamManagedProps={stripDatastreamManagedProps}
							clampToMinMax={clampToMinMax}
							applyDatastreamDefaultColors={applyDatastreamDefaultColors}
							remapColorStopsForDatastream={remapColorStopsForDatastream}
							getDeviceTemplateSignature={getDeviceTemplateSignature}
							periodOptions={periodOptions}
							periodStats={periodStats}
							getDsMinMax={getDsMinMax}
							isStringDatastream={isStringDatastream}
							isNumericDatastream={isNumericDatastream}
							getEffectiveMinMax={getEffectiveMinMax}
							normalizeSliderValueInputChange={normalizeSliderValueInputChange}
							snapValueToStep={snapValueToStep}
							updateWidgetLivePropsById={updateWidgetLivePropsById}
							directionLabels={directionLabels}
							setAlertMessage={setAlertMessage}
							setAlertType={setAlertType}
							setShowAlert={setShowAlert}
						/>
						<WidgetSettingsPreview
							edit={edit}
							dims={dims}
							deviceDatastream={deviceDatastream}
							validDatastreams={validDatastreams}
							findDatastreamByReference={findDatastreamByReference}
							preserveTitleSpaces={preserveTitleSpaces}
							resizeWidget={resizeWidget}
							defaultDivHeight={defaultDivHeight}
							defaultDivWidth={defaultDivWidth}
							handleTerminalCleared={handleTerminalCleared}
							updateWidgetLivePropsById={updateWidgetLivePropsById}
							commitWidgetPropsById={commitWidgetPropsById}
							defaultWidgetPropsByType={defaultWidgetPropsByType}
						/>
					</WidgetSettingsContent>
					<WidgetSettingsFooter
						t={t}
						onCancel={handleCancelWidgetSettings}
						onSave={handleSaveWidgetSettings}
					/>
				</WidgetSettingsModal>
				{
					mode === 'edit' && permissions['edit_templates'] ?
						<Fragment>
							<WidgetToolbox
								t={t}
								widgetSearchQuery={widgetSearchQuery}
								setWidgetSearchQuery={setWidgetSearchQuery}
								filteredWidgetOptions={filteredWidgetOptions}
							/>

							<EditDashboardCanvas
								t={t}
								dashboard={dashboard}
								devices={devices}
								validDatastreams={validDatastreams}
								divs={divs}
								isFixedWidthMode={isFixedWidthMode}
								fixedWidthGuide={fixedWidthGuide}
								isPercentageLayout={isPercentageLayout}
								editableWaterNetworkState={editableWaterNetworkState}
								multiSelectEnabled={multiSelectEnabled}
								selectedWidgetIdSet={selectedWidgetIdSet}
								selectedGroupDragOffset={selectedGroupDragOffset}
								selectionRect={selectionRect}
								selectedWidgetBounds={selectedWidgetBounds}
								selectedWidgetIds={selectedWidgetIds}
								duplicateSelectedGroup={duplicateSelectedGroup}
								removeSelectedGroup={removeSelectedGroup}
								toggleSelectedGroupFreeMovement={toggleSelectedGroupFreeMovement}
								selectedGroupFreeMovementEnabled={selectedGroupFreeMovementEnabled}
								widgetLiveProps={widgetLiveProps}
								edit={edit}
								defaultDivHeight={defaultDivHeight}
								defaultDivWidth={defaultDivWidth}
								layoutMode={layoutMode}
								handleDashboardDragOver={handleDashboardDragOver}
								drop={drop}
								handleEditablePointerDown={handleEditablePointerDown}
								handleEditableClickCapture={handleEditableClickCapture}
								startSelectedGroupDrag={startSelectedGroupDrag}
								getWidgetRectPx={getWidgetRectPx}
								findDatastreamByReference={findDatastreamByReference}
								formatPinForTitle={formatPinForTitle}
								preserveTitleSpaces={preserveTitleSpaces}
								resizeWidget={resizeWidget}
								setEdit={setEdit}
								setDeviceDatastream={setDeviceDatastream}
								handleTerminalCleared={handleTerminalCleared}
								duplicate={duplicate}
								remove={remove}
								updateWidgetLivePropsById={updateWidgetLivePropsById}
								commitWidgetPropsById={commitWidgetPropsById}
							/>
						</Fragment> :
						<ViewDashboardCanvas
							dashboard={dashboard}
							devices={devices}
							validDatastreams={validDatastreams}
							did={did}
							tid={tid}
							isFixedWidthMode={isFixedWidthMode}
							fixedViewWidth={fixedViewWidth}
							isPercentageLayout={isPercentageLayout}
							viewPercentageCanvas={viewPercentageCanvas}
							viewWaterNetworkState={viewWaterNetworkState}
							defaultDivHeight={defaultDivHeight}
							defaultDivWidth={defaultDivWidth}
							layoutMode={layoutMode}
							getWidgetRectPx={getWidgetRectPx}
							findDatastreamByReference={findDatastreamByReference}
							preserveTitleSpaces={preserveTitleSpaces}
							resizeWidget={resizeWidget}
							handleTerminalCleared={handleTerminalCleared}
							duplicate={duplicate}
							remove={remove}
							defaultWidgetPropsByType={defaultWidgetPropsByType}
						/>
				}
			</WebDashboardMainContainer>

			<WebDashboardFeedback
				t={t}
				removeBackgroundDialog={removeBackgroundDialog}
				removeBackgroundDialogMessage={removeBackgroundDialogMessage}
				continueRemoveBackground={continueRemoveBackground}
				resetRemoveBackgroundDialog={resetRemoveBackgroundDialog}
				showAlert={showAlert}
				alertType={alertType}
				alertPosition={alertPosition}
				alertMessage={alertMessage}
				setShowAlert={setShowAlert}
			/>
		</>
	);
};

export default WebDashboard;
